import React from 'react';
import { useDispatch } from 'react-redux';
import { ArcStack, ArcSwitch, ArcText } from '@src/core/ui';
import { setManualControl } from '@src/features/drone/redux';
import { EnRouteCommandEnum, EnRouteEventTypeEnum } from '@src/features/order/pages/take-off-to-en-route-page/entities';
import { ArcSocketContext } from '@src/core/socket/base';
import { EnRouteManualControlProps } from './types';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import { useFlightProvider } from '@src/features/order/provider';
import { useGoHere } from '@src/features/drone/context/go-here-context';
import { clearActiveGoHere } from '@src/features/drone/utils/go-here-registry';
import { FlightModeEnum } from '@src/features/order/entities';

const EnRouteManualControl: React.FC<EnRouteManualControlProps> = (props) => {
  const { drone, missionId } = props;
  const dispatch = useDispatch();

  const { flightDataByMissionId } = useFlightProvider();
  const flightMode = missionId ? flightDataByMissionId?.[missionId]?.flightMode : undefined;
  const { arcSocket } = React.useContext(ArcSocketContext);
  const { confirm } = useConfirm();
  const { resetGoHere } = useGoHere();

  const dismissGoHere = React.useCallback(() => {
    resetGoHere();
    clearActiveGoHere(drone?.serialNumber);
  }, [resetGoHere, drone?.serialNumber]);

  const onToggle = async (_: unknown, checked: boolean) => {
    const ws = arcSocket();

    if (!ws || !drone?.serialNumber) return;

    if (checked) {
      const confirmed = await confirm(
        'The drone will exit autonomous flight and respond only to manual pilot inputs. Ensure a qualified operator is ready.',
        'Confirm Manual Control?'
      );
      if (!confirmed) {
        return;
      }
      
      const payload = {
        type: EnRouteEventTypeEnum.COMMAND,
        command: EnRouteCommandEnum.START_CONTROL,
        serialNumber: drone.serialNumber,
      };
      dismissGoHere();
      ws.send(JSON.stringify(payload));
    } else {
      // Show confirmation for CONTINUE command when turning off manual control
      const confirmed = await confirm(
        'The drone will resume its mission from the current position.',
        'Confirm Continue?'
      );
      if (!confirmed) {
        return;
      }
      
      const payload = {
        type: EnRouteEventTypeEnum.COMMAND,
        command: EnRouteCommandEnum.CONTINUE,
        serialNumber: drone.serialNumber,
      }; 
      dismissGoHere();
      ws.send(JSON.stringify(payload));
    }

    // Dispatch with serialNumber to store per-drone setting
    dispatch(setManualControl({ serialNumber: drone.serialNumber, isManualControl: checked }));
  };

  return (
    <ArcStack 
      spacing={1} 
      direction='row' 
      alignItems='center' 
      justifyContent='space-between'>
      <ArcText.caption color='text.primary' fontWeight={500}> 
        MANUAL CONTROL  
      </ArcText.caption>
      <ArcSwitch.brand checked={flightMode === FlightModeEnum.OFFBOARD} onChange={onToggle} />
    </ArcStack>
  );
};

export default EnRouteManualControl;